/**
 * Custom error classes for API operations
 */

/**
 * Base API Error class
 */
export class ApiError extends Error {
  constructor(message, statusCode = 500, details = null) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.details = details;
    this.timestamp = new Date().toISOString();
  }
}

/**
 * Authentication/Authorization Error (401, 403)
 */
export class AuthError extends ApiError {
  constructor(message = 'Authentication failed', statusCode = 401, details = null) {
    super(message, statusCode, details);
    this.name = 'AuthError';
  }
}

/**
 * Not Found Error (404)
 */
export class NotFoundError extends ApiError {
  constructor(message = 'Resource not found', details = null) {
    super(message, 404, details);
    this.name = 'NotFoundError';
  }
}

/**
 * Validation Error (400, 422)
 */
export class ValidationError extends ApiError {
  constructor(message = 'Validation failed', statusCode = 400, details = null) {
    super(message, statusCode, details);
    this.name = 'ValidationError';
  }
}

/**
 * Network Error (no response from server)
 */
export class NetworkError extends ApiError {
  constructor(message = 'Network request failed', details = null) {
    super(message, 0, details);
    this.name = 'NetworkError';
  }
}

/**
 * Server Error (5xx)
 */
export class ServerError extends ApiError {
  constructor(message = 'Server error occurred', statusCode = 500, details = null) {
    super(message, statusCode, details);
    this.name = 'ServerError';
  }
}

/**
 * Factory function to create appropriate error based on status code
 * @param {number} statusCode - HTTP status code
 * @param {string} message - Error message
 * @param {*} details - Additional error details
 * @returns {ApiError} Appropriate error instance
 */
export function createApiError(statusCode, message, details = null) {
  if (statusCode === 401 || statusCode === 403) {
    return new AuthError(message, statusCode, details);
  }

  if (statusCode === 404) {
    return new NotFoundError(message, details);
  }

  if (statusCode === 400 || statusCode === 422) {
    return new ValidationError(message, statusCode, details);
  }

  if (statusCode >= 500) {
    return new ServerError(message, statusCode, details);
  }

  return new ApiError(message, statusCode, details);
}
